import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import routerStore from '@app/src/common/store/RouterStore';
import SidebarStore from '@app/src/common/components/sidebar/SidebarStore';
import { SIDEBAR_ROUTES } from '@app/src/common/components/navigation/constants/navigationRoutes';
import HomeIcon from '@app/src/common/components/navigation/assets/Home.svg';

import './ModuleSelector.scss';

const HOME_LABEL_ID = 'app.navigation.home';

const ModuleSelector: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        SidebarStore.closeModuleSelector();
      }
    };
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current) {
        return;
      }
      if (containerRef.current.contains(target)) {
        return;
      }
      const trigger = document.querySelector('.app-sidebar__module-trigger');
      if (trigger && trigger.contains(target)) {
        return;
      }
      SidebarStore.closeModuleSelector();
    };
    const handleBlur = () => SidebarStore.closeModuleSelector();

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const accessibleRoutes = routerStore.routes
    .filter(route => route.navigationRoute && SIDEBAR_ROUTES[route.path])
    .sort(
      (a, b) => SIDEBAR_ROUTES[a.path].order - SIDEBAR_ROUTES[b.path].order
    );

  const currentPath = routerStore.location && routerStore.location.pathname;
  const isHomeActive = currentPath === '/' || !currentPath;

  const handleHomeClick = () => {
    SidebarStore.closeModuleSelector();
    routerStore.targetPath = null;
    window.location.href = '/#/';
  };

  const handleModuleClick = (path: string) => {
    SidebarStore.closeModuleSelector();
    routerStore.navigate(path);
  };

  return createPortal(
    <div className="module-selector" ref={containerRef} role="dialog">
      <div className="module-selector__caret" />
      <button
        type="button"
        onClick={handleHomeClick}
        className={classNames('module-selector__tile', {
          'module-selector__tile_active': isHomeActive
        })}
      >
        <span className="module-selector__tile-icon">
          <HomeIcon />
        </span>
        <span className="module-selector__tile-label">
          <FormattedMessage id={HOME_LABEL_ID} />
        </span>
      </button>
      {accessibleRoutes.map(route => {
        const config = SIDEBAR_ROUTES[route.path];
        const Icon = config.Icon;
        const isActive = currentPath && currentPath.startsWith(route.path);
        return (
          <button
            type="button"
            key={route.path}
            onClick={() => handleModuleClick(route.path)}
            className={classNames('module-selector__tile', {
              'module-selector__tile_active': isActive
            })}
          >
            <span className="module-selector__tile-icon">
              <Icon />
            </span>
            <span className="module-selector__tile-label">
              <FormattedMessage id={config.labelId} />
            </span>
          </button>
        );
      })}
    </div>,
    document.body
  );
};

export default observer(ModuleSelector);
